import React, { useState } from 'react';
import { Allergy, ReviewStatus } from '../types/Allergy';
import './AllergyCard.css';

interface AllergyCardProps {
  allergy: Allergy;
  onReview: (id: string, status: ReviewStatus) => void;
}

const AllergyCard: React.FC<AllergyCardProps> = ({ allergy, onReview }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [viewImage, setViewImage] = useState<string | null>(null);
  const [viewFhir, setViewFhir] = useState(false);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleAction = (e: React.MouseEvent, status: ReviewStatus) => {
    e.stopPropagation();
    onReview(allergy.id, status);
  };

  return (
    <div className={`allergy-row ${isExpanded ? 'expanded' : ''} ${allergy.reviewStatus}`}>
      <div className="row-header" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="allergy-main-info">
          <span className="allergy-name">{allergy.allergyName}</span>
          <span className="allergy-source">via {allergy.sourceSystem}</span>
        </div>
        
        <div className="allergy-reaction hide-mobile">
          {allergy.reaction}
        </div>

        <div className="hide-mobile">
          <span className={`badge badge-severity-${allergy.severity.toLowerCase()}`}>
            {allergy.severity}
          </span>
        </div>

        <div className="hide-mobile">
          <span className="badge badge-status">{allergy.status}</span>
        </div>

        <div className="expand-icon">
          {allergy.reviewStatus === 'pending' ? (
            <span className="review-badge review-pending">Review Required ▼</span>
          ) : (
            <span className={`review-badge review-${allergy.reviewStatus}`}>
              {allergy.reviewStatus === 'approved' ? 'Approved ✓' : 'Rejected ✗'}
            </span>
          )}
        </div>
      </div>

      {isExpanded && (
        <div className="row-content">
          <div className="details-grid">
            <div className="info-section">
              <h4>Clinical Details</h4>
              <div className="info-item">
                <span className="info-label">Reaction Description</span>
                <span className="info-value">{allergy.reaction}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Onset Date</span>
                <span className="info-value">{formatDate(allergy.onsetDate)}</span>
              </div>
              {allergy.notes && (
                <div className="info-item">
                  <span className="info-label">Provider Notes</span>
                  <span className="info-value">{allergy.notes}</span>
                </div>
              )}
              
              <h4 style={{marginTop: '1.5rem'}}>Evidence Documentation</h4>
              <div className="evidence-grid">
                {allergy.evidenceScreenshots.map((shot) => (
                  <div key={shot.id} className="evidence-item">
                    <img 
                      src={shot.url} 
                      alt={shot.caption}
                      className="evidence-img"
                      onClick={() => setViewImage(shot.url)}
                    />
                    <div className="evidence-caption">
                      {shot.caption} • {formatDate(shot.timestamp)}
                    </div>
                  </div>
                ))}
              </div>

              {allergy.fhirResource && (
                <>
                  <h4 style={{marginTop: '1.5rem'}}>FHIR Resource</h4>
                  <button 
                    className="fhir-view-btn"
                    onClick={() => setViewFhir(true)}
                    style={{
                      padding: '0.75rem 1.25rem',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    📋 View FHIR Resource
                  </button>
                </>
              )}
            </div>

            <div className="action-section">
              {allergy.reviewStatus === 'pending' ? (
                <div className="action-panel">
                  <h4>Review Action</h4>
                  <p style={{fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '1rem'}}>
                    Please review the evidence provided and confirm if this allergy should be added to the EHR.
                  </p>
                  <button 
                    className="action-btn btn-approve"
                    onClick={(e) => handleAction(e, 'approved')}
                  >
                    ✓ Approve & Add
                  </button>
                  <button 
                    className="action-btn btn-reject"
                    onClick={(e) => handleAction(e, 'rejected')}
                  >
                    ✗ Reject Finding
                  </button>
                </div>
              ) : (
                <div className={`status-banner ${allergy.reviewStatus}`}>
                  This item has been {allergy.reviewStatus}.
                  <div style={{fontSize: '0.8rem', marginTop: '0.5rem', opacity: 0.8}}>
                    Processed on {new Date().toLocaleDateString()}
                  </div>
                  <button 
                    style={{
                      background: 'none', 
                      border: 'none', 
                      textDecoration: 'underline', 
                      color: 'inherit',
                      fontSize: '0.8rem',
                      marginTop: '0.5rem',
                      cursor: 'pointer'
                    }}
                    onClick={(e) => handleAction(e, 'pending')}
                  >
                    Undo
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {viewImage && (
        <div className="lightbox" onClick={() => setViewImage(null)}>
          <img src={viewImage} alt="Evidence detail" onClick={(e) => e.stopPropagation()} />
        </div>
      )}

      {viewFhir && (
        <div className="lightbox" onClick={() => setViewFhir(false)}>
          <div 
            className="fhir-modal" 
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '2rem',
              maxWidth: '800px',
              maxHeight: '80vh',
              overflow: 'auto',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            }}
          >
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '1.5rem',
              borderBottom: '2px solid #f0f0f0',
              paddingBottom: '1rem',
            }}>
              <h3 style={{ margin: 0, color: '#333' }}>FHIR AllergyIntolerance Resource</h3>
              <button 
                onClick={() => setViewFhir(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: '#666',
                  padding: '0.25rem 0.5rem',
                }}
              >
                ✕
              </button>
            </div>
            <pre style={{ 
              background: '#f8f9fa',
              padding: '1.5rem',
              borderRadius: '8px',
              overflow: 'auto',
              fontSize: '0.85rem',
              lineHeight: '1.6',
              color: '#2d3748',
              border: '1px solid #e2e8f0',
            }}>
              {JSON.stringify(allergy.fhirResource, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllergyCard;
